/**
 * RestrictedAccountGate — the firm-but-fair account-restricted notice (state F
 * in the fraud/dispute epic §5.5).
 *
 * Rendered only when a CRITICAL restriction is open on the current billable
 * account. It states the situation factually (via the restriction's neutral
 * `userFacingTk`), NEVER using the word "fraud", and always offers a real
 * appeal path — a contact-support CTA that opens the existing support dialog.
 *
 * Data-driven like the banners: the headline is fixed neutral copy, the detail
 * line is the restriction's own message (tk + params). New critical reasons ship
 * as DB metadata + a locale key with zero change here.
 */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccountRestrictions } from "@/hooks/useAccountRestrictions";
import { useSupportDialogStore } from "@/stores/useSupportDialogStore";
import { formatCreditsExact } from "@/utils/formatCredits";
import type { AccountRestriction } from "@/services/billingApi";

function formatParams(
  params: AccountRestriction["params"],
): Record<string, string> {
  if (!params) return {};
  const out: Record<string, string> = {};
  if (typeof params.frozenCredits === "number")
    out.frozenCredits = formatCreditsExact(params.frozenCredits);
  if (typeof params.availableCredits === "number")
    out.availableCredits = formatCreditsExact(params.availableCredits);
  if (typeof params.outstandingCredits === "number")
    out.outstandingCredits = formatCreditsExact(params.outstandingCredits);
  return out;
}

export function RestrictedAccountGate({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { criticalRestriction } = useAccountRestrictions();
  const openSupport = useSupportDialogStore((s) => s.open);

  const handleAppeal = useCallback(() => {
    openSupport();
  }, [openSupport]);

  if (!criticalRestriction) return null;

  const detail = t(
    criticalRestriction.userFacingTk,
    formatParams(criticalRestriction.params),
  );

  return (
    <div
      role="alert"
      data-testid="restricted-account-gate"
      className={
        className ??
        "flex flex-col gap-3 rounded-[8px] border border-destructive/40 bg-destructive/10 p-5"
      }
    >
      <div className="flex items-start gap-3">
        <ShieldAlert
          className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
          aria-hidden="true"
        />
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold text-foreground">
            {t("account.restricted.title")}
          </h3>
          <p className="text-sm text-muted-foreground">{detail}</p>
          <p className="text-sm text-muted-foreground">
            {t("account.restricted.appealPrompt")}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleAppeal}
          data-testid="restricted-account-appeal"
        >
          <LifeBuoy className="h-4 w-4" aria-hidden="true" />
          {t("account.restricted.contactSupport")}
        </Button>
      </div>
    </div>
  );
}
